// selectors.ts
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";

const addedItemArray = (state: RootState) => state.pos.current.addedItemArray;
const card = (state: RootState) => state.pos.current.card;

const cash = (state: RootState) => state.pos.current.cash;
const bank = (state: RootState) => state.pos.current.bank;
const commonDiscount = (state: RootState) => state.pos.current.commonDiscount;
const commonDiscountType = (state: RootState) =>
  state.pos.current.commonDiscountType || "amount";
const defaultTaxEnabled = (state: RootState) => state.pos.defaultTaxEnabled;
const defaultTaxPercentage = (state: RootState) =>
  state.pos.defaultTaxPercentage;

const roundMoney = (value: number) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const parseBillDiscount = (
  discountValue: number | string,
  discountType: "amount" | "percent",
  baseAmount: number,
): number => {
  if (discountValue == null || discountValue === "") return 0;

  const raw = String(discountValue).trim();
  if (raw === "") return 0;

  if (discountType === "percent") {
    const pct = parseFloat(raw);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return roundMoney(Math.max(0, (baseAmount * pct) / 100));
  }

  const amount = parseFloat(raw);
  return Number.isFinite(amount) ? roundMoney(Math.max(0, amount)) : 0;
};

export const discount = createSelector([addedItemArray], (addedItemArray) =>
  addedItemArray?.reduce(
    (sum: number, item: any) =>
      sum +
        (parseFloat(item?.discount) * parseFloat(item?.quantity || 0) || 0) ||
      0,
    0,
  ),
);

export const total = createSelector([addedItemArray], (addedItemArray) =>
  addedItemArray?.reduce(
    (sum: number, item: any) =>
      sum + parseFloat(item?.price || 0) * parseFloat(item?.quantity || 0) || 0,
    0,
  ),
);

export const tax = createSelector(
  [addedItemArray, defaultTaxEnabled, defaultTaxPercentage],
  (addedItemArray, taxEnabled, taxPct) =>
    addedItemArray?.reduce((sum: number, item: any) => {
      // Determine effective tax rate for this item:
      // use item's own tax if it's set, otherwise fall back to default tax (if enabled)
      const itemTax = parseFloat(item?.tax);
      const effectiveTax =
        itemTax > 0
          ? itemTax
          : taxEnabled && taxPct != null
            ? parseFloat(taxPct)
            : 0;

      return (
        sum +
          (effectiveTax *
            0.01 *
            ((parseFloat(item?.price) - parseFloat(item?.discount)) *
              parseFloat(item?.quantity || 0)) || 0) || 0
      );
    }, 0),
);

export const netTotal = createSelector(
  [total, discount, tax],
  (total, discount, tax) =>
    parseFloat(total) + parseFloat(tax) - parseFloat(discount) || 0,
);

export const billDiscountAmount = createSelector(
  [commonDiscount, commonDiscountType, netTotal],
  (commonDiscount, commonDiscountType, netTotal) =>
    parseBillDiscount(
      commonDiscount,
      commonDiscountType,
      Number(netTotal || 0),
    ),
);

export const balance = createSelector(
  [card, cash, bank, billDiscountAmount, netTotal],
  (card, cash, bank, billDiscountAmount, netTotal) => {
    const tendered =
      parseFloat(card || 0) + parseFloat(cash || 0) + parseFloat(bank || 0);
    const payable = Number(netTotal || 0) - Number(billDiscountAmount || 0);
    return roundMoney(tendered - payable);
  },
);
export const pay = createSelector(
  [card, cash, bank, billDiscountAmount],
  (card, cash, bank, billDiscountAmount) =>
    roundMoney(
      parseFloat(card || 0) +
        parseFloat(cash || 0) +
        parseFloat(bank || 0) +
        Number(billDiscountAmount || 0),
    ),
);
export const crossTotal = createSelector(
  [total, discount, tax, billDiscountAmount],
  (total, discount, tax, billDiscountAmount) =>
    parseFloat(total) +
      parseFloat(tax) -
      parseFloat(discount) -
      Number(billDiscountAmount || 0) || 0,
);
export const crossPay = createSelector(
  [card, cash, bank],
  (card, cash, bank) =>
    parseFloat(card || 0) + parseFloat(cash || 0) + parseFloat(bank || 0),
);
